/**
 * Living drawings, step 2: puppet motion (pure maths, deterministic per creature).
 *
 * A pose is a transform around the creature's feet (bottom centre) plus two strip deformations the
 * renderer applies row by row (the image is drawn as horizontal strips):
 *   bend     horizontal offset of the top row (lower rows follow with (1-v)^2) → sway, wave, wiggle
 *   shuffle  offset of the bottom rows only (feet slide) → walking steps
 * Units: translations are fractions of the creature height, bend/shuffle fractions of its width.
 */
import { hash } from '../mailing';
import type { CreatureKind } from './types';

export type CreatureAction = 'intro' | 'jump' | 'wave' | 'twirl' | 'wiggle' | 'walk';
/** Actions a tap cycles through. */
export const TAP_ACTIONS: CreatureAction[] = ['jump', 'wave', 'twirl', 'wiggle'];

export const ACTION_MS: Record<CreatureAction, number> = {
  intro: 2200,
  jump: 950,
  wave: 1300,
  twirl: 900,
  wiggle: 800,
  walk: 2400,
};

export interface Pose {
  tx: number;
  ty: number;
  rot: number;
  sx: number;
  sy: number;
  bend: number;
  shuffle: number;
  /** 0 = eyes open, 1 = closed. */
  blink: number;
  /** Pupil direction (-1..1). */
  look: { x: number; y: number };
  /** Eyes squeezed happy (^ ^) — on landing / when tickled. */
  happy: number;
}

export interface Rig {
  seed: number;
  kind: CreatureKind;
}

export const rigFor = (id: string, kind: CreatureKind): Rig => ({ seed: hash(id), kind });

const TAU = Math.PI * 2;
const clamp = (v: number, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const easeOut = (t: number) => 1 - (1 - t) ** 3;
const bump = (t: number, a: number, b: number) => (t <= a || t >= b ? 0 : Math.sin(((t - a) / (b - a)) * Math.PI));
const damp = (t: number, freq: number, decay: number) => Math.sin(t * freq * TAU) * Math.exp(-t * decay);

/** Blink: 0..1 closed amount at time t (ms); every 2.6–4.4 s, sometimes a double blink. */
export function blinkAt(rig: Rig, t: number): number {
  const period = 2600 + (rig.seed % 1800);
  const phase = (rig.seed >>> 8) % period;
  const local = (t + phase) % period;
  const one = (x: number) => (x >= 0 && x < 150 ? Math.sin((x / 150) * Math.PI) : 0);
  const double = ((Math.floor((t + phase) / period) + rig.seed) % 3) === 0;
  return Math.max(one(local), double ? one(local - 220) : 0);
}

/** Idle loop: breathing squash/stretch + gentle sway (or a lazy step for walkers). */
export function idlePose(rig: Rig, t: number, reduced = false): Pose {
  const s = rig.seed;
  const pose: Pose = { tx: 0, ty: 0, rot: 0, sx: 1, sy: 1, bend: 0, shuffle: 0, blink: blinkAt(rig, t), look: { x: 0, y: 0 }, happy: 0 };
  pose.look.x = Math.sin(t / 1700 + (s % 7)) * 0.55;
  pose.look.y = Math.sin(t / 2300 + (s % 5)) * 0.25;
  if (reduced) return pose;
  const breathe = Math.sin((t / (2400 + (s % 500))) * TAU);
  pose.sy = 1 + breathe * 0.03;
  pose.sx = 1 - breathe * 0.022;
  const swayAmt = rig.kind === 'swayer' ? 0.07 : rig.kind === 'hopper' ? 0.035 : 0.025;
  pose.bend = Math.sin((t / 3200) * TAU + (s % 11)) * swayAmt;
  pose.rot = Math.sin((t / 4100) * TAU + (s % 13)) * 0.018;
  if (rig.kind === 'walker') {
    // tiny weight shift between the feet
    pose.shuffle = Math.sin((t / 1600) * TAU) * 0.02;
  }
  if (rig.kind === 'hopper') {
    // a little happy bounce every ~5 s
    const cyc = (t + (s % 5000)) % 5200;
    const h = bump(cyc, 0, 420);
    pose.ty += h * 0.06;
    pose.sy *= 1 + h * 0.04;
  }
  return pose;
}

/** Pose while an action plays (`p` = elapsed ms since it started), layered over the idle pose. */
export function actionPose(rig: Rig, action: CreatureAction, p: number, base: Pose): Pose {
  const d = ACTION_MS[action];
  const u = clamp(p / d);
  const o: Pose = { ...base, look: { ...base.look } };
  switch (action) {
    case 'jump': {
      // anticipation squash → stretch take-off → arc → land squash → wobble
      if (u < 0.16) {
        const k = easeOut(u / 0.16);
        o.sy = 1 - 0.2 * k;
        o.sx = 1 + 0.14 * k;
      } else if (u < 0.7) {
        const a = (u - 0.16) / 0.54;
        o.ty = Math.sin(a * Math.PI) * 0.42;
        const st = 1 - Math.abs(a - 0.5) * 2;
        o.sy = 1 + 0.12 * (1 - st);
        o.sx = 1 - 0.08 * (1 - st);
        o.rot = Math.sin(a * TAU) * 0.06;
        o.look.y = -0.6;
      } else {
        const l = (u - 0.7) / 0.3;
        const w = damp(l * 0.6, 2.2, 5);
        o.sy = 1 - 0.18 * (1 - l) + w * 0.06;
        o.sx = 1 + 0.12 * (1 - l) - w * 0.05;
        o.happy = bump(l, 0, 1);
      }
      break;
    }
    case 'wave': {
      const env = bump(u, 0, 1);
      o.bend = Math.sin(u * TAU * 3.2) * 0.12 * env;
      o.rot = Math.sin(u * TAU * 1.6) * 0.06 * env;
      o.happy = env;
      break;
    }
    case 'twirl': {
      const a = easeOut(u);
      o.sx = Math.cos(a * TAU) * base.sx;
      o.ty = bump(u, 0, 1) * 0.12;
      o.happy = bump(u, 0.3, 1);
      break;
    }
    case 'wiggle': {
      const env = bump(u, 0, 1);
      o.bend = Math.sin(u * TAU * 6) * 0.08 * env;
      o.sx = 1 + Math.sin(u * TAU * 6 + 1) * 0.05 * env;
      o.sy = 1 - Math.sin(u * TAU * 6 + 1) * 0.05 * env;
      o.happy = env;
      break;
    }
    case 'walk': {
      // two little hop-steps to the side and back
      const steps = 6;
      const k = u * steps;
      const step = k % 1;
      o.tx = Math.sin(u * TAU) * 0.35;
      o.ty = Math.abs(Math.sin(step * Math.PI)) * 0.06;
      o.rot = Math.sin(k * Math.PI) * 0.05;
      o.shuffle = Math.sin(k * Math.PI) * 0.06;
      o.look.x = Math.cos(u * TAU) > 0 ? 0.8 : -0.8;
      break;
    }
    case 'intro': {
      // asleep on the paper → eyes open → stretch → happy hop
      if (u < 0.3) {
        o.blink = 1;
        o.sy = 0.94;
        o.sx = 1.04;
        o.bend = 0;
      } else if (u < 0.5) {
        const a = (u - 0.3) / 0.2;
        o.blink = a < 0.35 ? 1 - a / 0.35 : blinkAt(rig, p * 3) * 0.6;
        o.sy = 0.94 + 0.14 * Math.sin(a * Math.PI * 0.5);
        o.sx = 1.04 - 0.08 * Math.sin(a * Math.PI * 0.5);
        o.look.x = Math.sin(a * TAU) * 0.8;
      } else if (u < 0.62) {
        const a = (u - 0.5) / 0.12;
        o.sy = 1.08 - 0.26 * easeOut(a);
        o.sx = 0.96 + 0.18 * easeOut(a);
        o.blink = 0;
      } else if (u < 0.86) {
        const a = (u - 0.62) / 0.24;
        o.ty = Math.sin(a * Math.PI) * 0.5;
        o.sy = 1.1 - 0.1 * a;
        o.sx = 0.93 + 0.07 * a;
        o.rot = Math.sin(a * TAU) * 0.08;
        o.look.y = -0.5;
      } else {
        const l = (u - 0.86) / 0.14;
        const w = damp(l * 0.6, 2.2, 5);
        o.sy = 1 - 0.16 * (1 - l) + w * 0.06;
        o.sx = 1 + 0.1 * (1 - l) - w * 0.05;
        o.happy = 1;
      }
      break;
    }
  }
  return o;
}

/** Full pose at time `t` (ms) with an optional running action. */
export function puppetPose(rig: Rig, t: number, action?: { name: CreatureAction; start: number } | null, reduced = false): Pose {
  const base = idlePose(rig, t, reduced);
  if (!action || reduced) return action?.name === 'intro' && reduced ? { ...base, happy: 1 } : base;
  const p = t - action.start;
  if (p < 0 || p > ACTION_MS[action.name]) return base;
  return actionPose(rig, action.name, p, base);
}

/** Horizontal offset (fraction of width) of the row at `v` (0 = top, 1 = feet). */
export function stripOffset(pose: Pose, v: number): number {
  const top = (1 - v) ** 2;
  const feet = v > 0.78 ? (v - 0.78) / 0.22 : 0;
  return pose.bend * top + pose.shuffle * feet;
}

/** Ground shadow scale for a hop height (1 = on the ground). */
export const shadowScale = (ty: number) => clamp(1 - ty * 1.2, 0.35, 1);

/* -------------------------------- names -------------------------------- */

const SYL_A = ['Bo', 'Pi', 'Mo', 'Lu', 'Ti', 'Nu', 'Zu', 'Ki', 'Co', 'Fi', 'Ru', 'Da', 'Mi', 'Pu', 'Wi', 'Sa'];
const SYL_B = ['bo', 'po', 'mi', 'lu', 'ki', 'ni', 'zu', 'pip', 'do', 'mo', 'bi', 'la', 'to', 'nu', 'fi', 'ko'];

/** A short, cute, language-neutral creature name (deterministic per id). */
export function creatureName(id: string): string {
  const h = hash('name:' + id);
  const a = SYL_A[h % SYL_A.length]!;
  const b = SYL_B[(h >>> 5) % SYL_B.length]!;
  return a + b;
}
